// Timeline de auditoria do pedido — mostra tudo que aconteceu desde a
// criação. Só admin/gerente veem essa seção no OrderDetail.

import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { UserCog, Pencil, Trash2, Archive, ArrowRight, Sparkles, Plus } from 'lucide-react'
import { fmtMoeda } from '../utils/fmt'

const LABELS_CAMPOS = {
  'cliente.nome':      'Nome do cliente',
  'cliente.contato':   'Telefone',
  'cliente.email':     'E-mail',
  'cliente.documento': 'CPF/CNPJ',
  'cliente.endereco':  'Endereço',
  'cliente.cidade':    'Cidade',
  'cliente.bairro':    'Bairro',
  'numero':            'Número do pedido',
  'prazo':             'Prazo de entrega',
  'dataVenda':         'Data da venda',
  'status':            'Status',
  'valorTotal':        'Valor total',
  'frete':             'Frete',
  'vendedora':         'Vendedora',
  'observacoesGerais': 'Observações',
  'moveis.qtd':        'Quantidade de móveis',
}

function fmtValor(campo, v) {
  if (v === null || v === undefined || v === '') return '(vazio)'
  if (campo === 'valorTotal' || campo === 'frete') return fmtMoeda(Number(v) || 0)
  return String(v)
}

function iconePorTipo(tipo) {
  if (tipo === 'criacao')      return { Icon: Plus,    cor: 'text-emerald-600', bg: 'bg-emerald-100' }
  if (tipo === 'edicao')       return { Icon: Pencil,  cor: 'text-amber-600',   bg: 'bg-amber-100' }
  if (tipo === 'exclusao')     return { Icon: Trash2,  cor: 'text-red-600',     bg: 'bg-red-100' }
  if (tipo === 'restauracao')  return { Icon: Archive, cor: 'text-blue-600',    bg: 'bg-blue-100' }
  return { Icon: Sparkles, cor: 'text-slate-500', bg: 'bg-slate-100' }
}

function labelTipo(tipo) {
  if (tipo === 'criacao')     return 'Cadastrou o pedido'
  if (tipo === 'edicao')      return 'Editou'
  if (tipo === 'exclusao')    return 'Enviou pra lixeira'
  if (tipo === 'restauracao') return 'Restaurou da lixeira'
  return tipo
}

export function HistoricoPedido({ pedido }) {
  const historico = pedido.historico || []

  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 mb-4">
        <UserCog size={16} className="text-slate-500" />
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
          Histórico ({historico.length})
        </h3>
      </div>

      {historico.length === 0 ? (
        <p className="text-sm text-slate-400 italic">
          Este pedido não tem registros de auditoria — foi criado antes desse recurso ser ativado.
        </p>
      ) : (
        <div className="space-y-3">
          {[...historico].reverse().map(entry => {
            const { Icon, cor, bg } = iconePorTipo(entry.tipo)
            return (
              <div key={entry.id} className="flex gap-3">
                <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center shrink-0`}>
                  <Icon size={14} className={cor} />
                </div>
                <div className="flex-1 min-w-0 pb-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-700">{labelTipo(entry.tipo)}</p>
                    {entry.autorRole && (
                      <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                        {entry.autorRole}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <strong>{entry.autor}</strong>
                    {' · '}
                    {(() => {
                      try { return format(new Date(entry.timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) }
                      catch { return entry.timestamp }
                    })()}
                  </p>

                  {entry.mudancas && entry.mudancas.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {entry.mudancas.map((m, i) => (
                        <div key={i} className="text-xs bg-slate-50 rounded-lg px-3 py-2">
                          <p className="font-semibold text-slate-600 mb-0.5">
                            {LABELS_CAMPOS[m.campo] || m.campo}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap font-mono text-[11px]">
                            <span className="text-red-600 line-through break-all">{fmtValor(m.campo, m.antes)}</span>
                            <ArrowRight size={10} className="text-slate-400" />
                            <span className="text-emerald-700 font-bold break-all">{fmtValor(m.campo, m.depois)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {entry.motivo && (
                    <p className="text-xs text-slate-500 italic mt-1">"{entry.motivo}"</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
